/**
 * Typed client for the Cloudflare Worker API.
 *
 * Every request carries the Supabase access token. The bring-your-own AI key is
 * passed as a header for a single request and is never written to storage.
 */

import type { DiagnosisReport, InputTypeSelection, MeResponse, QuotaStatus } from '@core/types';
import { webConfig } from './config';

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly hint: string | null;
  readonly requestId: string | null;
  readonly details: Record<string, unknown> | null;

  constructor(input: {
    message: string;
    code: string;
    status: number;
    hint?: string | null;
    requestId?: string | null;
    details?: Record<string, unknown> | null;
  }) {
    super(input.message);
    this.name = 'ApiError';
    this.code = input.code;
    this.status = input.status;
    this.hint = input.hint ?? null;
    this.requestId = input.requestId ?? null;
    this.details = input.details ?? null;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  token: string | null;
  /** Only used for the diagnosis endpoint; never persisted. */
  aiKey?: { apiKey: string; baseUrl?: string; model?: string } | null;
  idempotencyKey?: string;
  signal?: AbortSignal;
}

interface ErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    hint?: string;
    details?: Record<string, unknown>;
    requestId?: string;
  };
}

export async function apiRequest<T>(path: string, options: RequestOptions): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (options.token) headers.authorization = `Bearer ${options.token}`;
  if (options.idempotencyKey) headers['idempotency-key'] = options.idempotencyKey;
  if (options.aiKey?.apiKey) {
    headers['x-ai-api-key'] = options.aiKey.apiKey;
    if (options.aiKey.baseUrl) headers['x-ai-base-url'] = options.aiKey.baseUrl;
    if (options.aiKey.model) headers['x-ai-model'] = options.aiKey.model;
  }

  let response: Response;
  try {
    response = await fetch(`${webConfig.apiBaseUrl}${path}`, {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError({
      code: 'network_error',
      status: 0,
      message:
        'The API could not be reached. Check the connection and the VITE_API_BASE_URL setting.',
    });
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let parsed: unknown = null;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }

  if (!response.ok) {
    const envelope = (parsed ?? {}) as ErrorEnvelope;
    throw new ApiError({
      code: envelope.error?.code ?? 'request_failed',
      status: response.status,
      message: envelope.error?.message ?? `The request failed with status ${response.status}.`,
      hint: envelope.error?.hint ?? null,
      requestId: envelope.error?.requestId ?? response.headers.get('x-request-id'),
      details: envelope.error?.details ?? null,
    });
  }

  return (parsed ?? {}) as T;
}

/* -------------------------------------------------------------------------- */
/* Endpoint helpers                                                           */
/* -------------------------------------------------------------------------- */

export interface DiagnosisSubmission {
  input: string;
  inputType: InputTypeSelection;
  openApiSpecId: string | null;
  customSensitiveFields: string[];
}

export interface DiagnosisResponse {
  diagnosis: {
    id: string;
    createdAt: string;
    detectedInputType: string;
    status: string;
    expiresAt: string | null;
    report: DiagnosisReport;
    sanitizedInput: string;
  };
  quota: QuotaStatus;
  replayed: boolean;
  notices: string[];
  providerNote?: string;
}

export interface SpecSummary {
  id: string;
  name: string;
  description: string | null;
  sourceFormat: 'json' | 'yaml';
  contentHash: string;
  title: string | null;
  version: string | null;
  openapi: string | null;
  operationCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ShareSummary {
  id: string;
  diagnosisId: string;
  expiresAt: string;
  revokedAt: string | null;
  createdAt: string;
  diagnosisSummary: string | null;
}

export interface HistorySummary {
  id: string;
  createdAt: string;
  detectedInputType: string;
  status: string;
  expiresAt: string | null;
  summary: string;
  topCause: { title: string; kind: string; confidence: number } | null;
  counts: { confirmed: number; likely: number; speculative: number };
  providerName: string;
}

export const api = {
  me: (token: string | null) => apiRequest<MeResponse>('/api/v1/me', { token }),

  updateProfile: (token: string | null, body: { displayName: string | null }) =>
    apiRequest<{ profile: { displayName: string | null } }>('/api/v1/me', {
      token,
      method: 'PATCH',
      body,
    }),

  deleteAccount: (token: string | null) =>
    apiRequest<{ deleted: boolean; removed: Record<string, number>; note: string }>('/api/v1/me', {
      token,
      method: 'DELETE',
    }),

  createDiagnosis: (
    token: string | null,
    submission: DiagnosisSubmission,
    options: { idempotencyKey: string; aiKey?: RequestOptions['aiKey']; signal?: AbortSignal },
  ) =>
    apiRequest<DiagnosisResponse>('/api/v1/diagnoses', {
      token,
      method: 'POST',
      body: submission,
      idempotencyKey: options.idempotencyKey,
      ...(options.aiKey ? { aiKey: options.aiKey } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    }),

  listDiagnoses: (token: string | null, limit = 25, offset = 0) =>
    apiRequest<{
      diagnoses: HistorySummary[];
      quota: QuotaStatus;
      retention: { persistent: boolean; retentionDays: number | null };
    }>(`/api/v1/diagnoses?limit=${limit}&offset=${offset}`, { token }),

  getDiagnosis: (token: string | null, id: string) =>
    apiRequest<DiagnosisResponse>(`/api/v1/diagnoses/${encodeURIComponent(id)}`, { token }),

  deleteDiagnosis: (token: string | null, id: string) =>
    apiRequest<void>(`/api/v1/diagnoses/${encodeURIComponent(id)}`, { token, method: 'DELETE' }),

  createShare: (token: string | null, diagnosisId: string, expiresInDays: number) =>
    apiRequest<{ share: { id: string; expiresAt: string; token: string; url: string } }>(
      `/api/v1/diagnoses/${encodeURIComponent(diagnosisId)}/share`,
      { token, method: 'POST', body: { expiresInDays } },
    ),

  listShares: (token: string | null) =>
    apiRequest<{ shares: ShareSummary[] }>('/api/v1/shares', { token }),

  revokeShare: (token: string | null, id: string) =>
    apiRequest<{ revoked: boolean }>(`/api/v1/shares/${encodeURIComponent(id)}/revoke`, {
      token,
      method: 'POST',
      body: {},
    }),

  listSpecs: (token: string | null) =>
    apiRequest<{ specs: SpecSummary[]; limit: number; used: number }>('/api/v1/specs', { token }),

  createSpec: (
    token: string | null,
    body: { name: string; description: string | null; content: string },
  ) =>
    apiRequest<{ spec: SpecSummary; warnings: string[] }>('/api/v1/specs', {
      token,
      method: 'POST',
      body,
    }),

  updateSpec: (
    token: string | null,
    id: string,
    body: { name?: string; description?: string | null; content?: string },
  ) =>
    apiRequest<{ spec: SpecSummary }>(`/api/v1/specs/${encodeURIComponent(id)}`, {
      token,
      method: 'PUT',
      body,
    }),

  deleteSpec: (token: string | null, id: string) =>
    apiRequest<void>(`/api/v1/specs/${encodeURIComponent(id)}`, { token, method: 'DELETE' }),

  startCheckout: (token: string | null) =>
    apiRequest<{ checkoutUrl: string | null; sessionId: string }>('/api/v1/billing/checkout', {
      token,
      method: 'POST',
      body: {},
    }),

  openPortal: (token: string | null) =>
    apiRequest<{ portalUrl: string | null }>('/api/v1/billing/portal', {
      token,
      method: 'POST',
      body: {},
    }),

  publicReport: (shareToken: string) =>
    apiRequest<{
      report: DiagnosisReport;
      sanitizedInput: string;
      createdAt: string;
      sharedBy: string;
      expiresAt: string;
    }>(`/api/v1/public/reports/${encodeURIComponent(shareToken)}`, { token: null }),

  health: () =>
    apiRequest<{
      status: string;
      environment: string;
      billingConfigured: boolean;
      platformAiEnabled: boolean;
      mockProviderAllowed: boolean;
      devAuthEnabled: boolean;
      missingConfiguration: string[];
    }>('/api/v1/health', { token: null }),

  devSession: () =>
    apiRequest<{ token: string; user: { id: string; email: string } }>('/api/v1/dev/session', {
      token: null,
      method: 'POST',
      body: {},
    }),
};
